import React, { useContext, useState, useEffect } from 'react';
import { FBAInboundContext } from '../FBAInboundContext';
import { Truck, MapPin, DollarSign, CheckCircle, Loader2, RefreshCw, Clock, Package, Box } from 'lucide-react';
import * as api from '../services/fbaInboundApi';

const Step4_PlacementSelection = () => {
  const {
    planId,
    generatePlacement,
    selectPlacement,
    placementOptions,
    selectedPlacementOption,
    nextStep,
    loading,
    statusMessage,
    markStepCompleted
  } = useContext(FBAInboundContext);

  const [step, setStep] = useState(placementOptions.length > 0 ? 'select' : 'generate');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [verifyingSelection, setVerifyingSelection] = useState(false);
  const [shipmentDetails, setShipmentDetails] = useState({}); // Map of placementOptionId -> shipments array
  const [loadingShipments, setLoadingShipments] = useState(false);

  // Fetch shipment details for all placement options
  const fetchShipmentDetails = async () => {
    if (!planId || placementOptions.length === 0) return;

    setLoadingShipments(true);
    const details = {};

    for (const option of placementOptions) {
      const placementOptionId = option.placement_option_id;
      if (!placementOptionId) continue;

      try {
        const result = await api.getShipmentDetails(planId, placementOptionId);
        details[placementOptionId] = result.data || [];
      } catch (err) {
        console.warn(`Failed to get shipment details for ${placementOptionId}:`, err);
        details[placementOptionId] = [];
      }
    }

    setShipmentDetails(details);
    setLoadingShipments(false);
  };

  // Auto-switch to select step when placement options are loaded
  useEffect(() => {
    if (placementOptions.length > 0) {
      setStep('select');
      setIsGenerating(false);

      // Auto-select cheapest option if none selected
      if (!selectedOptionId) {
        // Sort to find cheapest
        const sorted = [...placementOptions].sort((a, b) => {
          const getFee = (opt) => {
            if (Array.isArray(opt.fees) && opt.fees.length > 0) {
              return parseFloat(opt.fees[0]?.value?.amount) || 0;
            }
            return parseFloat(opt.fees?.total) || 0;
          };
          return getFee(a) - getFee(b);
        });
        if (sorted[0]) {
          setSelectedOptionId(sorted[0].id);
        }
      }

      // Fetch shipment details
      fetchShipmentDetails();
    }
  }, [placementOptions, planId]);

  // Mark step as completed when placement is already selected
  useEffect(() => {
    if (selectedPlacementOption) {
      markStepCompleted(4);
    }
  }, [selectedPlacementOption, markStepCompleted]);

  const handleGeneratePlacement = async () => {
    setIsGenerating(true);
    try {
      await generatePlacement();
      setStep('select');
    } catch (err) {
      console.error('Failed to generate placement:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectPlacement = async () => {
    if (!selectedOptionId) return;

    // Find the selected option to get the Amazon placement_option_id
    const selectedOption = placementOptions.find(opt => opt.id === selectedOptionId);
    if (!selectedOption) {
      console.error('Selected option not found');
      return;
    }

    // Use placement_option_id (Amazon's ID), not id (internal ID)
    const amazonPlacementOptionId = selectedOption.placement_option_id;
    if (!amazonPlacementOptionId) {
      console.error('No placement_option_id found on selected option:', selectedOption);
      return;
    }

    console.log('Selecting placement option:', amazonPlacementOptionId);

    setIsSelecting(true);
    setVerifyingSelection(true);

    try {
      await selectPlacement(amazonPlacementOptionId);
      setTimeout(() => {
        nextStep();
      }, 1500);
    } catch (err) {
      console.error('Failed to select placement:', err);
      setVerifyingSelection(false);
    } finally {
      setIsSelecting(false);
    }
  };

  // Success state
  if (selectedPlacementOption) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Placement Option Selected
        </h3>
        <p className="text-gray-600">
          Amazon has confirmed your placement selection.
        </p>
      </div>
    );
  }

  // Verifying selection state
  if (verifyingSelection) {
    return (
      <div className="text-center py-12">
        <Clock className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-pulse" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Verifying Selection
        </h3>
        <p className="text-gray-600">
          Waiting for Amazon to confirm your placement selection...
        </p>
        <p className="text-sm text-gray-500 mt-2">
          This typically takes 1-4 seconds
        </p>
      </div>
    );
  }

  // Generate placement step
  if (step === 'generate') {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Generate Placement Options
          </h3>
          <p className="text-gray-600 max-w-md mx-auto">
            Amazon will analyze your shipment and provide placement options
            showing different destinations and associated fees.
          </p>
        </div>

        <button
          onClick={handleGeneratePlacement}
          disabled={isGenerating || loading}
          className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center"
        >
          {isGenerating ? (
            <>
              <div className="flex items-center">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {statusMessage || 'Generating Options...'}
              </div>
            </>
          ) : (
            <div className="flex items-center">
              <RefreshCw className="w-5 h-5 mr-2" />
              Generate Placement Options
            </div>
          )}
        </button>
      </div>
    );
  }

  // Select placement step
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Truck className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h4 className="font-medium text-blue-900">Select Placement Option</h4>
            <p className="text-sm text-blue-700 mt-1">
              Choose where Amazon should route your shipment. Different options may have
              different fees and delivery destinations.
            </p>
          </div>
        </div>
      </div>

      {/* Placement Options - sorted by cheapest first */}
      <div className="space-y-4">
        {[...placementOptions]
          .sort((a, b) => {
            // Extract fee from fees array: fees[0].value.amount
            const getFee = (opt) => {
              if (Array.isArray(opt.fees) && opt.fees.length > 0) {
                return parseFloat(opt.fees[0]?.value?.amount) || 0;
              }
              return parseFloat(opt.fees?.total) || parseFloat(opt.total_fee) || 0;
            };
            return getFee(a) - getFee(b);
          })
          .map((option, index) => {
            // Extract fee from fees array
            let totalFee = 0;
            let currency = 'USD';
            if (Array.isArray(option.fees) && option.fees.length > 0) {
              totalFee = parseFloat(option.fees[0]?.value?.amount) || 0;
              currency = option.fees[0]?.value?.code || 'USD';
            } else if (option.fees?.total) {
              totalFee = parseFloat(option.fees.total) || 0;
            }

            const isCheapest = index === 0;
            const optionNumber = index + 1;

            return (
              <button
                key={option.id}
                onClick={() => setSelectedOptionId(option.id)}
                className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                  selectedOptionId === option.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 flex-wrap gap-2">
                      <span className="font-medium text-gray-900">
                        Option {optionNumber}
                      </span>
                      {isCheapest && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold bg-green-500 text-white rounded-full">
                          Cheapest
                        </span>
                      )}
                      <span className="inline-flex items-center px-2 py-1 text-sm font-semibold rounded bg-gray-100 text-gray-700">
                        <DollarSign className="w-4 h-4 mr-0.5" />
                        {totalFee.toFixed(2)} {currency}
                      </span>
                    </div>

                    {/* Shipments from fetched details */}
                    {(() => {
                      const placementOptionId = option.placement_option_id;
                      const shipments = shipmentDetails[placementOptionId] || [];
                      const numShipments = option.shipment_ids?.length || shipments.length || 0;

                      return (
                        <div className="mt-3">
                          {/* Shipment count badge */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                              <Package className="w-3 h-3 mr-1" />
                              {numShipments} {numShipments === 1 ? 'Shipment' : 'Shipments'}
                            </span>
                            {loadingShipments && (
                              <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                            )}
                          </div>

                          {/* Shipment destinations */}
                          {shipments.length > 0 && (
                            <div className="space-y-2">
                              {shipments.map((shipment, idx) => {
                                const dest = shipment.destination;
                                const warehouseId = dest?.warehouse_id || 'Unknown';
                                const city = dest?.address?.city || '';
                                const state = dest?.address?.state_or_province_code || '';
                                const boxCount = shipment.boxes?.length || shipment.box_groups?.length || 0;

                                return (
                                  <div
                                    key={idx}
                                    className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-2"
                                  >
                                    <div className="flex items-center">
                                      <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                                      <span className="font-medium text-gray-900">{warehouseId}</span>
                                      {city && (
                                        <span className="text-gray-500 ml-2">
                                          {city}{state ? `, ${state}` : ''}
                                        </span>
                                      )}
                                    </div>
                                    {boxCount > 0 && (
                                      <span className="inline-flex items-center text-xs text-gray-500">
                                        <Box className="w-3 h-3 mr-1" />
                                        {boxCount} {boxCount === 1 ? 'box' : 'boxes'}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Fallback if no details yet but we have shipment_ids */}
                          {shipments.length === 0 && numShipments > 0 && !loadingShipments && (
                            <div className="text-xs text-gray-400 italic">
                              {numShipments} destination{numShipments > 1 ? 's' : ''} (loading details...)
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Fees Breakdown */}
                    {Array.isArray(option.fees) && option.fees.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="text-xs text-gray-500 space-y-1">
                          {option.fees.map((fee, feeIdx) => (
                            <div key={feeIdx} className="flex justify-between">
                              <span>{fee.target || fee.description?.substring(0, 50)}</span>
                              <span className="font-medium">${fee.value?.amount} {fee.value?.code}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedOptionId === option.id && (
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 ml-4" />
                  )}
                </div>
              </button>
            );
          })}
      </div>

      {/* Empty State */}
      {placementOptions.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
          <Truck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No placement options available yet.</p>
          <button
            onClick={handleGeneratePlacement}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            Generate Options
          </button>
        </div>
      )}

      {/* Submit Button */}
      {placementOptions.length > 0 && (
        <button
          onClick={handleSelectPlacement}
          disabled={!selectedOptionId || isSelecting || loading}
          className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSelecting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Selecting...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              Select Placement Option
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default Step4_PlacementSelection;
